/**
 * Resource Service
 * 
 * Provides comprehensive resource management functionality including:
 * - CRUD operations for resources
 * - File upload and management
 * - Category management
 * - View and download tracking
 * - Statistics and analytics
 * 
 * @module ResourceService
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  QueryConstraint,
  Timestamp,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  UploadMetadata
} from 'firebase/storage';
import { db, storage } from '../config/firebase';
import { getAuth } from 'firebase/auth';
import {
  Resource,
  CreateResourceData,
  UpdateResourceData,
  ResourceFilters,
  ResourceSort,
  ResourceActionResult,
  ResourceStatistics,
  ResourceCategory,
  ResourceType,
  FileMetadata,
  CategoryInfo
} from '../@types/resource';

/**
 * Resource Service Class
 */
export class ResourceService {
  private static readonly RESOURCES_COLLECTION = 'resources';
  private static readonly STORAGE_PATH = 'resources';

  /**
   * Category definitions with icons and colors
   */
  static readonly CATEGORIES: CategoryInfo[] = [
    { id: 'training', name: 'Training Materials', description: 'Training guides and tutorials', icon: '🎓', color: '#3B82F6' },
    { id: 'product-info', name: 'Product Information', description: 'Product specs and details', icon: '📦', color: '#10B981' },
    { id: 'sales-materials', name: 'Sales Materials', description: 'Sales presentations and pitches', icon: '💼', color: '#8B5CF6' },
    { id: 'marketing', name: 'Marketing', description: 'Marketing materials and campaigns', icon: '📢', color: '#F59E0B' },
    { id: 'policies', name: 'Policies', description: 'Company policies and guidelines', icon: '📋', color: '#EF4444' },
    { id: 'procedures', name: 'Procedures', description: 'Standard operating procedures', icon: '⚙️', color: '#6366F1' },
    { id: 'forms', name: 'Forms', description: 'Business forms and templates', icon: '📝', color: '#EC4899' },
    { id: 'guides', name: 'Guides', description: 'How-to guides and manuals', icon: '📖', color: '#14B8A6' },
    { id: 'presentations', name: 'Presentations', description: 'PowerPoint and slide decks', icon: '🎬', color: '#F97316' },
    { id: 'other', name: 'Other', description: 'Miscellaneous resources', icon: '📁', color: '#64748B' }
  ];

  /**
   * Get all resources with filtering and sorting
   * 
   * @param filters - Optional filters
   * @param sort - Optional sort configuration
   * @param pageSize - Number of results per page
   * @returns Promise resolving to resources
   */
  static async getResources(
    filters?: ResourceFilters,
    sort?: ResourceSort,
    pageSize: number = 50
  ): Promise<{ resources: Resource[]; hasMore: boolean }> {
    try {
      const resourcesRef = collection(db, this.RESOURCES_COLLECTION);
      const constraints: QueryConstraint[] = [];

      // Apply filters
      if (filters) {
        if (filters.category) {
          if (Array.isArray(filters.category)) {
            constraints.push(where('category', 'in', filters.category));
          } else {
            constraints.push(where('category', '==', filters.category));
          }
        }

        if (filters.type) {
          if (Array.isArray(filters.type)) {
            constraints.push(where('type', 'in', filters.type));
          } else {
            constraints.push(where('type', '==', filters.type));
          }
        }

        if (filters.status) {
          constraints.push(where('status', '==', filters.status));
        }

        if (filters.visibility && filters.visibility !== 'all') {
          constraints.push(where('visibility', '==', filters.visibility));
        }

        if (filters.featured !== undefined) {
          constraints.push(where('featured', '==', filters.featured));
        }

        if (filters.createdBy) {
          constraints.push(where('createdBy', '==', filters.createdBy));
        }

        if (filters.createdByName) {
          constraints.push(where('createdByName', '==', filters.createdByName));
        }

        if (filters.createdAfter) {
          constraints.push(where('createdAt', '>=', Timestamp.fromDate(filters.createdAfter)));
        }

        if (filters.createdBefore) {
          constraints.push(where('createdAt', '<=', Timestamp.fromDate(filters.createdBefore)));
        }
      }

      // Apply sorting - IMPORTANT: orderBy must come after where clauses
      // If we have a where clause on status and orderBy on createdAt, we need a composite index
      const sortField = sort?.field || 'createdAt';
      const sortDirection = sort?.direction || 'desc';
      
      // Only add orderBy if we're not using multiple where clauses that require index
      // For now, we'll add it and rely on the index being created
      constraints.push(orderBy(sortField, sortDirection));

      // Add limit
      constraints.push(limit(pageSize + 1));

      const q = query(resourcesRef, ...constraints);
      const querySnapshot = await getDocs(q);

      const resources: Resource[] = [];
      const docs = querySnapshot.docs;
      const hasMore = docs.length > pageSize;

      const docsToProcess = hasMore ? docs.slice(0, -1) : docs;

      for (const docSnap of docsToProcess) {
        const data = docSnap.data();
        resources.push(this.formatResourceData(docSnap.id, data));
      }

      // Apply client-side filters
      let filteredResources = resources;

      // Handle visibility filtering for 'all' case
      if (filters?.visibility === 'all') {
        filteredResources = resources.filter(resource =>
          !resource.visibility || // If visibility is not set, show to all users
          resource.visibility === 'all' || 
          resource.visibility === 'sales-reps-only'
        );
      }

      if (filters?.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        filteredResources = filteredResources.filter(resource =>
          resource.title.toLowerCase().includes(searchLower) ||
          resource.description.toLowerCase().includes(searchLower) ||
          resource.tags?.some(tag => tag.toLowerCase().includes(searchLower))
        );
      }

      if (filters?.tags && filters.tags.length > 0) {
        filteredResources = filteredResources.filter(resource =>
          resource.tags?.some(tag => filters.tags!.includes(tag))
        );
      }

      return {
        resources: filteredResources,
        hasMore
      };
    } catch (error: any) {
      console.error('Error fetching resources:', error);
      throw new Error(`Failed to fetch resources: ${error.message}`);
    }
  }

  /**
   * Get a single resource by ID
   * 
   * @param resourceId - Resource ID
   * @returns Promise resolving to resource or null
   */
  static async getResourceById(resourceId: string): Promise<Resource | null> {
    try {
      if (!resourceId) {
        throw new Error('Resource ID is required');
      }

      const resourceRef = doc(db, this.RESOURCES_COLLECTION, resourceId);
      const resourceSnap = await getDoc(resourceRef);

      if (!resourceSnap.exists()) {
        console.warn(`Resource not found: ${resourceId}`);
        return null;
      }

      const data = resourceSnap.data();
      return this.formatResourceData(resourceId, data);
    } catch (error: any) {
      console.error('Error fetching resource:', error);
      throw new Error(`Failed to fetch resource: ${error.message}`);
    }
  }

  /**
   * Create a new resource
   * 
   * @param resourceData - Resource creation data
   * @param createdBy - User ID of creator
   * @param createdByName - Name of creator
   * @param files - Optional files to upload
   * @returns Promise resolving to action result
   */
  static async createResource(
    resourceData: CreateResourceData,
    createdBy: string,
    createdByName: string,
    files?: File[]
  ): Promise<ResourceActionResult> {
    try {
      // Validate required fields
      if (!resourceData.title) {
        throw new Error('Resource title is required');
      }
      if (!resourceData.description) {
        throw new Error('Resource description is required');
      }

      // Upload files if provided
      let fileMetadata: FileMetadata[] = [];
      if (files && files.length > 0) {
        fileMetadata = await this.uploadFiles(files, createdBy);
      }

      // Prepare resource document
      const resourceDoc = {
        title: resourceData.title,
        description: resourceData.description,
        category: resourceData.category,
        type: resourceData.type,
        status: resourceData.status || 'published',
        visibility: resourceData.visibility || 'all',
        textContent: resourceData.textContent || '',
        linkUrl: resourceData.linkUrl || '',
        files: fileMetadata,
        tags: resourceData.tags || [],
        featured: resourceData.featured || false,
        viewCount: 0,
        downloadCount: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        publishedAt: resourceData.status === 'published' ? serverTimestamp() : null,
        createdBy,
        createdByName
      };

      const resourcesRef = collection(db, this.RESOURCES_COLLECTION);
      const docRef = await addDoc(resourcesRef, resourceDoc);

      console.log('Resource created successfully:', docRef.id);

      // Trigger notification for resource added (only if published)
      const resourceStatus = resourceData.status || 'published';
      if (resourceStatus === 'published') {
        try {
          const { NotificationTriggerService } = await import('./NotificationTriggerService');
          await NotificationTriggerService.triggerResourceAddedNotification(
            docRef.id,
            resourceData.title,
            resourceData.type
          ).catch(err => {
            console.error('Failed to trigger resource notification:', err);
            // Don't fail resource creation if notification fails
          });
        } catch (error) {
          console.error('Error importing notification service:', error);
          // Don't fail resource creation if notification fails
        }
      }

      return {
        success: true,
        resourceId: docRef.id,
        message: 'Resource created successfully'
      };
    } catch (error: any) {
      console.error('Error creating resource:', error);
      return {
        success: false,
        error: error.message || 'Failed to create resource'
      };
    }
  }

  /**
   * Update an existing resource
   * 
   * @param resourceId - Resource ID
   * @param resourceData - Update data
   * @param updatedBy - User ID of updater
   * @param updatedByName - Name of updater
   * @param newFiles - Optional new files to upload
   * @returns Promise resolving to action result
   */
  static async updateResource(
    resourceId: string,
    resourceData: UpdateResourceData,
    updatedBy: string,
    updatedByName: string,
    newFiles?: File[]
  ): Promise<ResourceActionResult> {
    try {
      if (!resourceId) {
        throw new Error('Resource ID is required');
      }

      const resourceRef = doc(db, this.RESOURCES_COLLECTION, resourceId);
      
      // Check if resource exists
      const resourceSnap = await getDoc(resourceRef);
      if (!resourceSnap.exists()) {
        throw new Error('Resource not found');
      }

      const currentData = resourceSnap.data();

      // Upload new files if provided
      let updatedFiles = currentData.files || [];
      if (newFiles && newFiles.length > 0) {
        const newFileMetadata = await this.uploadFiles(newFiles, updatedBy);
        updatedFiles = [...updatedFiles, ...newFileMetadata];
      }

      // Prepare update data
      const updateData: any = {
        updatedAt: serverTimestamp(),
        updatedBy,
        updatedByName
      };

      if (resourceData.title !== undefined) updateData.title = resourceData.title;
      if (resourceData.description !== undefined) updateData.description = resourceData.description;
      if (resourceData.category !== undefined) updateData.category = resourceData.category;
      if (resourceData.type !== undefined) updateData.type = resourceData.type;
      if (resourceData.status !== undefined) {
        updateData.status = resourceData.status;
        if (resourceData.status === 'published' && !currentData.publishedAt) {
          updateData.publishedAt = serverTimestamp();
          
          // Trigger notification when resource is published for the first time
          try {
            const { NotificationTriggerService } = await import('./NotificationTriggerService');
            await NotificationTriggerService.triggerResourceAddedNotification(
              resourceId,
              currentData.title || resourceData.title || 'Untitled Resource',
              resourceData.type || currentData.type
            ).catch(err => {
              console.error('Failed to trigger resource notification:', err);
              // Don't fail resource update if notification fails
            });
          } catch (error) {
            console.error('Error importing notification service:', error);
            // Don't fail resource update if notification fails
          }
        }
      }
      if (resourceData.visibility !== undefined) updateData.visibility = resourceData.visibility;
      if (resourceData.textContent !== undefined) updateData.textContent = resourceData.textContent;
      if (resourceData.linkUrl !== undefined) updateData.linkUrl = resourceData.linkUrl;
      if (resourceData.tags !== undefined) updateData.tags = resourceData.tags;
      if (resourceData.featured !== undefined) updateData.featured = resourceData.featured;
      if (resourceData.thumbnailUrl !== undefined) updateData.thumbnailUrl = resourceData.thumbnailUrl;
      if (newFiles && newFiles.length > 0) updateData.files = updatedFiles;

      await updateDoc(resourceRef, updateData);

      console.log('Resource updated successfully:', resourceId);

      return {
        success: true,
        resourceId,
        message: 'Resource updated successfully'
      };
    } catch (error: any) {
      console.error('Error updating resource:', error);
      return {
        success: false,
        error: error.message || 'Failed to update resource'
      };
    }
  }

  /**
   * Delete a resource
   * 
   * @param resourceId - Resource ID
   * @param deletedBy - User ID of deleter
   * @returns Promise resolving to action result
   */
  static async deleteResource(
    resourceId: string,
    deletedBy: string
  ): Promise<ResourceActionResult> {
    try {
      if (!resourceId) {
        throw new Error('Resource ID is required');
      }

      const resourceRef = doc(db, this.RESOURCES_COLLECTION, resourceId);
      
      // Check if resource exists
      const resourceSnap = await getDoc(resourceRef);
      if (!resourceSnap.exists()) {
        throw new Error('Resource not found');
      }

      // Delete associated files from storage
      const data = resourceSnap.data();
      if (data.files && data.files.length > 0) {
        await this.deleteFiles(data.files);
      }

      // Delete the resource document
      await deleteDoc(resourceRef);

      console.log('Resource deleted successfully:', resourceId);

      return {
        success: true,
        resourceId,
        message: 'Resource deleted successfully'
      };
    } catch (error: any) {
      console.error('Error deleting resource:', error);
      return {
        success: false,
        error: error.message || 'Failed to delete resource'
      };
    }
  }

  /**
   * Upload files to Firebase Storage
   * 
   * @param files - Files to upload
   * @param userId - User ID (Firestore document ID)
   * @returns Promise resolving to file metadata array
   * @private
   */
  private static async uploadFiles(files: File[], userId: string): Promise<FileMetadata[]> {
    const fileMetadata: FileMetadata[] = [];
    
    // Get the current Firebase Auth UID for storage permissions
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User must be authenticated to upload files');
    }
    const authUid = currentUser.uid;
    
    console.log('ResourceService uploadFiles - Debug info:', {
      firestoreUserId: userId,
      firebaseAuthUid: authUid,
      storagePath: `${this.STORAGE_PATH}/${authUid}`
    });

    for (const file of files) {
      try {
        const timestamp = Date.now();
        const fileName = `${timestamp}_${file.name}`;
        // Use Firebase Auth UID for storage path to match security rules
        const storageRef = ref(storage, `${this.STORAGE_PATH}/${authUid}/${fileName}`);

        const metadata: UploadMetadata = {
          contentType: file.type,
          customMetadata: {
            uploadedBy: userId, // Keep the Firestore document ID for reference
            originalName: file.name
          }
        };

        await uploadBytes(storageRef, file, metadata);
        const downloadURL = await getDownloadURL(storageRef);

        fileMetadata.push({
          name: file.name,
          size: file.size,
          type: file.type,
          url: downloadURL,
          uploadedAt: Timestamp.now()
        });

        console.log('File uploaded successfully:', fileName);
      } catch (error: any) {
        console.error('Error uploading file:', file.name, error);
        throw new Error(`Failed to upload file ${file.name}: ${error.message}`);
      }
    }

    return fileMetadata;
  }

  /**
   * Delete files from Firebase Storage
   * 
   * @param fileMetadata - Files to delete
   * @private
   */
  private static async deleteFiles(fileMetadata: FileMetadata[]): Promise<void> {
    for (const file of fileMetadata) {
      try {
        const storageRef = ref(storage, file.url);
        await deleteObject(storageRef);
        console.log('File deleted successfully:', file.name);
      } catch (error: any) {
        console.warn('Error deleting file:', file.name, error.message);
        // Don't throw - continue deleting other files
      }
    }
  }

  /**
   * Track resource view
   * 
   * @param resourceId - Resource ID
   * @param userId - User ID
   * @param userName - User name
   * @param userRole - User role
   */
  static async trackView(
    resourceId: string,
    userId: string,
    userName: string,
    userRole: string
  ): Promise<void> {
    try {
      // Increment view count
      const resourceRef = doc(db, this.RESOURCES_COLLECTION, resourceId);
      await updateDoc(resourceRef, {
        viewCount: increment(1)
      });

      // Log view event
      const viewsRef = collection(db, this.RESOURCES_COLLECTION, resourceId, 'views');
      await addDoc(viewsRef, {
        resourceId,
        userId,
        userName,
        userRole,
        viewedAt: serverTimestamp()
      });

      console.log('Resource view tracked:', resourceId);
    } catch (error: any) {
      console.warn('Failed to track view:', error.message);
      // Don't throw - view tracking is non-critical
    }
  }

  /**
   * Track resource download
   * 
   * @param resourceId - Resource ID
   * @param userId - User ID
   * @param userName - User name
   * @param userRole - User role
   * @param fileUrl - Downloaded file URL
   * @param fileName - Downloaded file name
   */
  static async trackDownload(
    resourceId: string,
    userId: string,
    userName: string,
    userRole: string,
    fileUrl: string,
    fileName: string
  ): Promise<void> {
    try {
      // Increment download count
      const resourceRef = doc(db, this.RESOURCES_COLLECTION, resourceId);
      await updateDoc(resourceRef, {
        downloadCount: increment(1)
      });

      // Log download event
      const downloadsRef = collection(db, this.RESOURCES_COLLECTION, resourceId, 'downloads');
      await addDoc(downloadsRef, {
        resourceId,
        userId,
        userName,
        userRole,
        fileUrl,
        fileName,
        downloadedAt: serverTimestamp()
      });

      console.log('Resource download tracked:', resourceId);
    } catch (error: any) {
      console.warn('Failed to track download:', error.message);
      // Don't throw - download tracking is non-critical
    }
  }

  /**
   * Get resource statistics
   * 
   * @returns Promise resolving to statistics
   */
  static async getResourceStatistics(): Promise<ResourceStatistics> {
    try {
      const resourcesRef = collection(db, this.RESOURCES_COLLECTION);
      const querySnapshot = await getDocs(resourcesRef);

      const stats: ResourceStatistics = {
        totalResources: 0,
        publishedResources: 0,
        draftResources: 0,
        archivedResources: 0,
        resourcesByCategory: {} as Record<ResourceCategory, number>,
        resourcesByType: {} as Record<ResourceType, number>,
        totalViews: 0,
        totalDownloads: 0,
        mostViewedResources: [],
        recentResources: []
      };

      const resources: Resource[] = [];

      querySnapshot.forEach((doc) => {
        const resource = this.formatResourceData(doc.id, doc.data());
        resources.push(resource);
        
        stats.totalResources++;

        // Count by status
        switch (resource.status) {
          case 'published':
            stats.publishedResources++;
            break;
          case 'draft':
            stats.draftResources++;
            break;
          case 'archived':
            stats.archivedResources++;
            break;
        }

        // Count by category
        stats.resourcesByCategory[resource.category] = 
          (stats.resourcesByCategory[resource.category] || 0) + 1;

        // Count by type
        stats.resourcesByType[resource.type] = 
          (stats.resourcesByType[resource.type] || 0) + 1;

        // Sum views and downloads
        stats.totalViews += resource.viewCount || 0;
        stats.totalDownloads += resource.downloadCount || 0;
      });

      // Get most viewed resources
      stats.mostViewedResources = resources
        .sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0))
        .slice(0, 5);

      // Get recent resources
      stats.recentResources = resources
        .sort((a, b) => {
          const aDate = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt);
          const bDate = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt);
          return bDate.getTime() - aDate.getTime();
        })
        .slice(0, 5);

      return stats;
    } catch (error: any) {
      console.error('Error calculating statistics:', error);
      throw new Error(`Failed to calculate statistics: ${error.message}`);
    }
  }

  /**
   * Get category information
   * 
   * @param categoryId - Category ID
   * @returns Category information or undefined
   */
  static getCategoryInfo(categoryId: ResourceCategory): CategoryInfo | undefined {
    return this.CATEGORIES.find(cat => cat.id === categoryId);
  }

  /**
   * Format resource data from Firestore
   * 
   * @param resourceId - Resource ID
   * @param data - Raw Firestore data
   * @returns Formatted Resource object
   * @private
   */
  private static formatResourceData(resourceId: string, data: any): Resource {
    return {
      id: resourceId,
      ...data,
      visibility: data.visibility || 'all', // Default to 'all' if not set
      createdAt: data.createdAt?.toDate?.() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      publishedAt: data.publishedAt?.toDate?.() || data.publishedAt,
      files: data.files?.map((file: any) => ({
        ...file,
        uploadedAt: file.uploadedAt?.toDate?.() || file.uploadedAt
      })) || []
    } as Resource;
  }
}

